import React, { useState } from 'react';

import Slider, { CustomArrowProps } from 'react-slick';
import Modal from '../common/Modal/Modal';
import { ImageResizer } from '../../Types/ImageResizer';

import { ReactComponent as CircleArrowRightIcon } from '../Icons/circleArrowRight.svg';
import { ReactComponent as CircleArrowLleftIcon } from '../Icons/circleArrowLeft.svg';
import { ReactComponent as Close } from '../Icons/close.svg';

import './Testimonials.scss';
import { useSessionStorage } from '../../hooks/useSessionStorage';

interface Tile {
	shortText: string;
	shortTextUa: string;
	text: string;
	clientReviewLogo: ImageResizer;
	clientReviewImage: ImageResizer;
	reasonImage?: ImageResizer;
	link: string;
	company: string;
	founder?: string;
}

interface TestemonialsProps {
	title: string;
	titleUa: string;
	tiles: Tile[];
}

const PrevArrow = ({ onClick }: CustomArrowProps) => {
	return (
		<div className="leftArrow" onClick={onClick}>
			{' '}
			<CircleArrowLleftIcon />{' '}
		</div>
	);
};

const NextArrow = ({ onClick }: CustomArrowProps) => {
	return (
		<div className="rightArrow" onClick={onClick}>
			{' '}
			<CircleArrowRightIcon />{' '}
		</div>
	);
};

export default function Testimonials({ title, titleUa, tiles }: TestemonialsProps) {
	const [currentTestimonialsTile, setCurrentTestimonialsTile] = useState<number>(0);
	const [testimonialsModal, setTestimonialsModal] = useState(false);
	const [language] = useSessionStorage('language', '');

	const settings = {
		className: 'center',
		centerMode: true,
		focusOnSelect: true,
		centerPadding: '180px',
		adaptiveHeight: true,
		afterChange: (index: number) => setCurrentTestimonialsTile(index),
		slidesToShow: 3,
		speed: 700,
		prevArrow: <PrevArrow />,
		nextArrow: <NextArrow />,
		responsive: [
			{
				breakpoint: 1400,
				settings: {
					slidesToShow: 3,
					centerPadding: '60px',
				},
			},
			{
				breakpoint: 1124,
				settings: {
					slidesToShow: 1,
					centerPadding: '360px',
				},
			},
			{
				breakpoint: 1060,
				settings: {
					slidesToShow: 1,
					centerPadding: '320px',
				},
			},
			{
				breakpoint: 1000,
				settings: {
					slidesToShow: 1,
					centerPadding: '280px',
				},
			},
			{
				breakpoint: 950,
				settings: {
					slidesToShow: 1,
					centerPadding: '240px',
				},
			},
			{
				breakpoint: 900,
				settings: {
					slidesToShow: 1,
					centerPadding: '200px',
				},
			},
			{
				breakpoint: 850,
				settings: {
					slidesToShow: 1,
					centerPadding: '160px',
				},
			},
			{
				breakpoint: 800,
				settings: {
					slidesToShow: 1,
					centerPadding: '120px',
				},
			},
			{
				breakpoint: 750,
				settings: {
					slidesToShow: 1,
					centerPadding: '100px',
				},
			},
			{
				breakpoint: 700,
				settings: {
					slidesToShow: 1,
					centerPadding: '80px',
				},
			},
			{
				breakpoint: 650,
				settings: {
					slidesToShow: 1,
					centerPadding: '60px',
				},
			},
			{
				breakpoint: 600,
				settings: {
					slidesToShow: 1,
					centerPadding: '50px',
				},
			},
			{
				breakpoint: 550,
				settings: {
					slidesToShow: 1,
					centerPadding: '40px',
				},
			},
			{
				breakpoint: 500,
				settings: {
					slidesToShow: 1,
					centerPadding: '30px',
				},
			},
			{
				breakpoint: 450,
				settings: {
					slidesToShow: 1,
					centerPadding: '20px',
				},
			},
			{
				breakpoint: 400,
				settings: {
					slidesToShow: 1,
					centerPadding: '10px',
				},
			},
			{
				breakpoint: 350,
				settings: {
					slidesToShow: 1,
					centerPadding: '0px',
				},
			},
		],
	};

	return (
		<div data-testclass="testimonialsSection" id="testimonialsSection" className="testimonialsContent">
			<div className="testimonialsTitle">{language === 'ua' ? titleUa : title}</div>
			<Slider {...settings}>
				{tiles.map((tile, index) => (
					<div
						key={index}
						onClick={() => (tile.reasonImage ? null : index === currentTestimonialsTile && setTestimonialsModal(true))}
						className="testimonialsTile"
					>
						<div
							className="testimonialsTileImage"
							style={{
								backgroundImage: tile.reasonImage
									? tile.reasonImage && `url(${tile.reasonImage.sm.src})`
									: tile.clientReviewImage && `url(${tile.clientReviewImage.sm.src})`,
							}}
						/>
						<div className="testimonialsTileContentWrapper">
							<div className="testimonialsTileContent">
								{tile.clientReviewLogo && (
									<div
										className="companyTileImage"
										style={{
											backgroundImage: tile.clientReviewLogo && `url(${tile.clientReviewLogo.sm.src})`,
										}}
									/>
								)}
								<div className={tile.reasonImage ? 'reasonTileTitle' : 'testimonialsTileTitle'}>
									{language === 'ua' ? tile.shortTextUa : tile.shortText}
								</div>
							</div>
						</div>
					</div>
				))}
			</Slider>
			<Modal isOpen={testimonialsModal} onRequestClose={() => setTestimonialsModal(false)} className="testimonialsModalOverlay">
				<div className="testimonialsModal">
					<div
						className="testimonialsModalImage"
						style={{
							backgroundImage:
								tiles[currentTestimonialsTile].clientReviewImage &&
								`url(${tiles[currentTestimonialsTile].clientReviewImage.original.src})`,
						}}
					>
						<div className="testimonialsModalContentWrapper">
							<div className="testimonialsModalContent">
								<div className="testimonialsModalTitle">
									<a href={tiles[currentTestimonialsTile].link} target="_blank" rel="noopener noreferrer">
										<div
											className="testimonialsModalLogo"
											style={{
												backgroundImage:
													tiles[currentTestimonialsTile].clientReviewLogo &&
													`url(${tiles[currentTestimonialsTile].clientReviewLogo.sm.src})`,
											}}
										/>
									</a>
									{tiles[currentTestimonialsTile].text}
									<div className="testimonialsModalFooter">
										<span>{tiles[currentTestimonialsTile].founder}</span>
										<span>{tiles[currentTestimonialsTile].company}</span>
									</div>
								</div>
							</div>
						</div>
						<button onClick={() => setTestimonialsModal(false)} className="closeTestimonialsModal">
							<Close />
						</button>
					</div>
				</div>
			</Modal>
		</div>
	);
}
